import React, { useEffect, useState } from "react";
import { api, formatCHF, formatNum, currentMonthString, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";

function parseHM(s) {
  if (!s) return null;
  const [h, m] = s.split(":").map(Number);
  return h * 60 + (m || 0);
}
function calcStunden(start, end, pauseMin) {
  const s = parseHM(start);
  const e = parseHM(end);
  if (s == null || e == null) return 0;
  let diff = e - s;
  if (diff < 0) diff += 24 * 60;
  diff -= Number(pauseMin) || 0;
  return Math.max(0, +(diff / 60).toFixed(2));
}

const EMPTY = {
  datum: new Date().toISOString().slice(0, 10),
  fahrer_id: "",
  bereich: "Tag",
  startzeit: "08:00",
  endzeit: "17:00",
  pause_minuten: 30,
  stunden: 8.5,
  stundenlohn_fahrer: 25.00,
  kundenansatz: 27.00,
  bemerkung: "",
  status: "offen",
};

export default function Spedition() {
  const [monat, setMonat] = useState(currentMonthString());
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [fahrerFilter, setFahrerFilter] = useState("_all");
  const [planUploading, setPlanUploading] = useState(false);
  const planFileRef = React.useRef();

  const load = async () => {
    const [r, e] = await Promise.all([
      api.get("/spedition", { params: { monat } }),
      api.get("/employees"),
    ]);
    setList(r.data); setEmployees(e.data);
  };
  useEffect(() => { load(); }, [monat]);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  const recalcStunden = () => {
    const h = calcStunden(form.startzeit, form.endzeit, form.pause_minuten);
    setForm((f) => ({ ...f, stunden: h }));
  };

  useEffect(() => {
    if (form.startzeit && form.endzeit) recalcStunden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.startzeit, form.endzeit, form.pause_minuten]);

  const save = async () => {
    try {
      const payload = {
        ...form,
        stunden: Number(form.stunden) || 0,
        pause_minuten: Number(form.pause_minuten) || 0,
        stundenlohn_fahrer: Number(form.stundenlohn_fahrer) || 0,
        kundenansatz: Number(form.kundenansatz) || 0,
      };
      if (editing) await api.put(`/spedition/${editing.id}`, payload);
      else await api.post("/spedition", payload);
      toast.success("Gespeichert");
      setOpen(false); load();
    } catch { toast.error("Fehler"); }
  };

  const uploadPlan = async (file) => {
    setPlanUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("monat", monat);
    try {
      const r = await api.post("/spedition/upload-plan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created} Einsätze erstellt · ${r.data.skipped} übersprungen${r.data.warnings.length ? ` · ${r.data.warnings.length} Hinweise` : ""}`);
      load();
    } catch (e) {
      toast.error("Plan-Upload fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setPlanUploading(false);
      if (planFileRef.current) planFileRef.current.value = "";
    }
  };

  const filteredList = fahrerFilter === "_all" ? list : list.filter((s) => s.fahrer_id === fahrerFilter);
  const totalH = filteredList.reduce((acc, s) => acc + (Number(s.stunden) || 0), 0);
  const totalLohn = filteredList.reduce((acc, s) => acc + (Number(s.stunden) || 0) * (Number(s.stundenlohn_fahrer) || 0), 0);
  const totalUmsatz = filteredList.reduce((acc, s) => acc + (Number(s.stunden) || 0) * (Number(s.kundenansatz) || 0), 0);

  return (
    <PageContainer>
      <PageHeader title="Spedition" subtitle="Tag-/Nacht-Einsätze" testId="spedition-page">
        <MonthPicker value={monat} onChange={setMonat} testId="sp-month-picker" />
        <input
          type="file"
          ref={planFileRef}
          className="hidden"
          accept="image/*,application/pdf"
          onChange={(e) => e.target.files?.[0] && uploadPlan(e.target.files[0])}
          data-testid="sp-plan-input"
        />
        <Button
          variant="outline"
          onClick={() => planFileRef.current?.click()}
          disabled={planUploading}
          data-testid="sp-plan-upload-button"
          className="border-neutral-300"
        >
          {planUploading ? <Sparkles className="h-4 w-4 mr-2 animate-pulse" /> : <Upload className="h-4 w-4 mr-2" />}
          {planUploading ? "KI parst…" : "Spedi-Aufwand hochladen"}
        </Button>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="add-spedition-button">
          <Plus className="h-4 w-4 mr-2" /> Neuer Einsatz
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="space-y-2 md:w-64">
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Fahrer-Filter</Label>
            <Select value={fahrerFilter} onValueChange={setFahrerFilter}>
              <SelectTrigger data-testid="sp-fahrer-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Alle Fahrer</SelectItem>
                {employees.filter((e) => e.aktiv).map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-3">
            <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Einsätze</div>
              <div className="text-lg font-semibold nivara-num">{filteredList.length}</div>
            </div>
            <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Total Std</div>
              <div className="text-lg font-semibold nivara-num">{totalH.toFixed(2)}</div>
            </div>
            <div className="text-center px-3 py-2 bg-neutral-50 rounded-md border border-neutral-200">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Umsatz</div>
              <div className="text-lg font-semibold nivara-num">{formatCHF(totalUmsatz)}</div>
            </div>
          </div>
        </div>
        {fahrerFilter !== "_all" && (
          <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-600">
            Fahrerlohn-Summe für {employees.find((e) => e.id === fahrerFilter)?.name}: <span className="font-mono font-semibold">{formatCHF(totalLohn)}</span>
          </div>
        )}
      </Card>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead><TableHead>Bereich</TableHead><TableHead>Mitarbeiter</TableHead>
                <TableHead>Start</TableHead><TableHead>Ende</TableHead><TableHead className="text-right">Pause</TableHead>
                <TableHead className="text-right">Stunden</TableHead><TableHead className="text-right">Umsatz</TableHead>
                <TableHead className="text-right">Lohn</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredList.length === 0 && (<TableRow><TableCell colSpan={10} className="text-center text-neutral-500 py-8">Keine Einsätze für die aktuelle Auswahl.</TableCell></TableRow>)}
              {filteredList.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono">{formatDatum(s.datum)}</TableCell>
                  <TableCell><Badge variant="outline">{s.bereich}</Badge></TableCell>
                  <TableCell>{empName(s.fahrer_id)}</TableCell>
                  <TableCell className="font-mono">{s.startzeit}</TableCell>
                  <TableCell className="font-mono">{s.endzeit}</TableCell>
                  <TableCell className="nivara-num">{s.pause_minuten} Min</TableCell>
                  <TableCell className="nivara-num">{formatNum(s.stunden, 2)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF((s.stunden||0) * (s.kundenansatz||0))}</TableCell>
                  <TableCell className="nivara-num">{formatCHF((s.stunden||0) * (s.stundenlohn_fahrer||0))}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={async () => { if(window.confirm("Löschen?")){ await api.delete(`/spedition/${s.id}`); load();}}}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="sp-dialog">
          <DialogHeader><DialogTitle>{editing ? "Einsatz bearbeiten" : "Neuer Spedition-Einsatz"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Datum</Label><Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2">
              <Label>Bereich</Label>
              <Select value={form.bereich} onValueChange={(v) => setForm({...form, bereich: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Tag">Tag</SelectItem><SelectItem value="Nacht">Nacht</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Mitarbeiter</Label>
              <Select value={form.fahrer_id} onValueChange={(v) => setForm({...form, fahrer_id: v})}>
                <SelectTrigger><SelectValue placeholder="Mitarbeiter wählen…" /></SelectTrigger>
                <SelectContent>{employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Startzeit</Label><Input type="time" value={form.startzeit} onChange={(e) => setForm({...form, startzeit: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Endzeit</Label><Input type="time" value={form.endzeit} onChange={(e) => setForm({...form, endzeit: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Pause (Min)</Label><Input type="number" value={form.pause_minuten} onChange={(e) => setForm({...form, pause_minuten: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Stunden (auto)</Label><Input type="number" step="0.01" value={form.stunden} onChange={(e) => setForm({...form, stunden: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Stundenlohn Fahrer (CHF)</Label><Input type="number" step="0.01" value={form.stundenlohn_fahrer} onChange={(e) => setForm({...form, stundenlohn_fahrer: e.target.value})} className="font-mono" data-testid="sp-lohn-input" /></div>
            <div className="space-y-2"><Label>Kundenansatz (CHF/h)</Label><Input type="number" step="0.01" value={form.kundenansatz} onChange={(e) => setForm({...form, kundenansatz: e.target.value})} className="font-mono" /></div>
            <Card className="sm:col-span-2 bg-neutral-50 p-3 border-neutral-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs text-neutral-500 uppercase tracking-wider">Umsatz</div><div className="font-mono font-semibold">{formatCHF((Number(form.stunden)||0) * (Number(form.kundenansatz)||0))}</div></div>
                <div><div className="text-xs text-neutral-500 uppercase tracking-wider">Fahrerlohn</div><div className="font-mono font-semibold">{formatCHF((Number(form.stunden)||0) * (Number(form.stundenlohn_fahrer)||0))}</div></div>
              </div>
            </Card>
            <div className="space-y-2 sm:col-span-2"><Label>Bemerkung</Label><Input value={form.bemerkung || ""} onChange={(e) => setForm({...form, bemerkung: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="sp-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
